import {useContext, useEffect, useState} from 'react'
import { notifyError } from '../../../utils/notifications'
import {Button, Empty, Modal, Table, Typography} from 'antd'
import {ConnectorContext} from './context/context'
import {getRequest, postRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import RecordsCount from '../../common/RecordsCount'

const CloneSvgModal = ({ open, onClose, onSuccess }) => {
  const intl = useIntl()
  const { vehicle } = useParams()
  const [state, dispatch] = useContext(ConnectorContext)
  const [, navDispatch] = useContext(NavContext)

  const [connectorsData, setConnectorsData] = useState([])
  const [selectedConnector, setSelectedConnector] = useState(null)
  const [loading, setLoading] = useState(false)

  const destination = state?.selected_connector || {}

  useEffect(() => {
    if (open && destination?.fpn) {
      fetchConnectorsForPartNumber()
    }
  }, [open])

  const fetchConnectorsForPartNumber = async () => {
    setLoading(true)
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'connectors.fetchingPartNumberData' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await getRequest(
      `/api/connector/part_number/search/?vehicle=${vehicle}&part_number=${destination.fpn}&connector=${destination.name}`
    )
    if (res?.status === false) {
      navDispatch({ type: 'loader', data: { showLoader: false }, notification: {} })
      notifyError(navDispatch, intl.formatMessage({ id: 'connectors.searchPartNumberError' }))
      setLoading(false)
      return
    }
    setConnectorsData(res?.data || [])
    setSelectedConnector(null)

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })
    setLoading(false)
  }

  const handleClone = () => {
    if (!selectedConnector) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'warning',
          msg: intl.formatMessage({ id: 'connectors.selectSourceConnector' }),
        },
      })
      return
    }

    Modal.confirm({
      title: intl.formatMessage({ id: 'connectors.confirmCloneSvgTitle' }),
      content: intl.formatMessage(
        { id: 'connectors.confirmCloneSvgContent' },
        { name: selectedConnector.name }
      ),
      centered: true,
      onOk: async () => {
        navDispatch({
          type: 'loader',
          data: {
            caption: intl.formatMessage(
              { id: 'connectors.cloningSvg' },
              { name: selectedConnector.name }
            ),
            showLoader: true,
          },
          notification: {},
        })

        let res = await postRequest(
          `/api/connector/clone/svg/?vehicle=${vehicle}`,
          {
            destination_connector: destination.id,
            source_connector: selectedConnector.id,
          }
        )

        // Clear the loader FIRST, and for both paths: the 'loader' reducer case
        // overwrites `notification`, so a toast dispatched before it is wiped in
        // the same batch. It used to run after the if/else and killed both.
        navDispatch({
          type: 'loader',
          data: { showLoader: false },
          notification: {},
        })

        if (res?.status) {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'success',
              msg: intl.formatMessage({ id: 'connectors.cloneSvgSuccess' }),
            },
          })
          onSuccess?.()
          handleClose()
        } else {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg: intl.formatMessage({ id: 'connectors.cloneSvgError' }),
            },
          })
        }
      },
    })
  }

  const handleClose = () => {
    setConnectorsData([])
    setSelectedConnector(null)
    onClose()
  }

  const columns = [
    {
      title: intl.formatMessage({ id: 'connectors.cloneName' }),
      dataIndex: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: intl.formatMessage({ id: 'connectors.clonePartNumber' }),
      dataIndex: 'fpn',
      sorter: (a, b) => (a.fpn || '').localeCompare(b.fpn || ''),
    },
    {
      title: intl.formatMessage({ id: 'connectors.cloneVehicle' }),
      dataIndex: 'vehicle_name',
      sorter: (a, b) => (a.vehicle_name || '').localeCompare(b.vehicle_name || ''),
    },
  ]

  return (
    <Modal
      open={open}
      title={
        <span>
          {intl.formatMessage({ id: 'connectors.cloneSvgModalTitle' })}{' '}
          <Typography.Text underline>
            {destination.name || ''}
          </Typography.Text>{' '}
          {intl.formatMessage({ id: 'connectors.cloneSvgModalAnd' })}{' '}
          <Typography.Text underline>
            {destination.fpn || ''}
          </Typography.Text>{' '}
          {intl.formatMessage({ id: 'connectors.cloneSvgModalPartNumber' })}
        </span>
      }
      onCancel={handleClose}
      width="70%"
      centered
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '0 24px 8px 24px' }}>
          <Button onClick={handleClose}>
            {intl.formatMessage({ id: 'forms.cancel' })}
          </Button>
          <Button
            type="primary"
            disabled={!selectedConnector}
            onClick={handleClone}
          >
            {intl.formatMessage({ id: 'connectors.cloneSelectedSvg' })}
          </Button>
        </div>
      }
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={connectorsData}
        loading={loading}
        pagination={false}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: selectedConnector ? [selectedConnector.id] : [],
          onChange: (selectedRowKeys, selectedRows) => {
            setSelectedConnector(selectedRows[0] || null)
          },
        }}
        locale={{
          emptyText: (
            <div style={{padding: '16px 0'}}>
              <Empty description={<RecordsCount count={0} msgId="connectors.noConnectorsForPartNumber"/>} />
            </div>
          ),
        }}
        scroll={{ y: '50vh' }}
      />
    </Modal>
  )
}

export default CloneSvgModal
